/**
 * FilterEditablePill — wraps WHERE-fragment text in an inline-editable input
 * that commits a `modifyFilter` edit on Enter / blur and cancels on Escape.
 *
 * Click on the text → editing mode (text → input); Enter / blur commits;
 * Escape reverts. Empty / unchanged text → no commit (no-op).
 *
 * PRD: Ralph/VisualQueryEditorEdit/PRD.md § Slice: Edit — add/modify/remove
 * filter (WHERE / HAVING).
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import type { ReactElement, RefObject } from 'react';

import type { BooleanClauseTarget } from '../../../../sql-resolver/types/structuralEdit';
import type { VqeEditContext } from '../../edit/editContext';
import { toCommitCtx } from '../../edit/editContext';
import { VqeTooltip } from '../common/VqeTooltip';
import { modifyFilter } from '../../edit/edits/modifyFilter';
import { sqlEngineToDatabaseEngineId } from '../../edit/livePullSchemaSnapshot';
import { resolveEditPredicate } from '../common/looseFkEditEnrichment';
import { useVqeFilterColumns } from '../common/useVqeFilterColumns';
import { VqeFilterChipBuilder } from '../common/VqeFilterChipBuilder';
import { escapeClaimProps } from '../../../common/utils/escapeClaimTarget';
import { PillValueLabel } from './FilterPillLabels';

export interface FilterEditablePillProps {
  readonly testId: string;
  readonly text: string;
  readonly fragmentIndex: number;
  readonly editContext: VqeEditContext;
  /** Target clause for the `modifyFilter` commit — `'where'` (default) or `'having'`. */
  readonly clause?: BooleanClauseTarget;
}

/**
 * Editing-mode body for the pill — decides chip vs. raw-text input using
 * `resolveEditPredicate`, the SAME resolvability check
 * `VqeFilterChipBuilder.tsx`'s `EditBuilder` uses internally, so this gate can
 * never diverge from what the builder can actually resolve.
 *
 * A genuinely separate, edit-time-only component (mirrors `RailLeafChip`'s
 * `RailLeafEditor`): its `useVqeFilterColumns` call (which pulls in the column
 * picker, MetaInfo, and pg-version queries) is heavy — running it on EVERY
 * idle, non-editing pill re-render was pure waste and a second live copy of the
 * unstable-hook pattern. Deferring it here means idle pills never touch the
 * resolver; the work runs only once the user clicks into edit mode.
 */
function FilterPillEditor({
  testId,
  text,
  value,
  onValueChange,
  inputRef,
  editContext,
  onChipCommit,
  onChipCancel,
  onInputCommit,
  onInputCancel,
}: {
  readonly testId: string;
  readonly text: string;
  readonly value: string;
  readonly onValueChange: (next: string) => void;
  readonly inputRef: RefObject<HTMLInputElement>;
  readonly editContext: VqeEditContext;
  readonly onChipCommit: (newText: string) => void;
  readonly onChipCancel: () => void;
  readonly onInputCommit: () => void;
  readonly onInputCancel: () => void;
}): ReactElement {
  // Engine-aware so JSON/enum/loose-FK predicates (PARITY-JSON-01,
  // PARITY-ENUM-01, PARITY-LOOSEFK-01) resolve to the structured chip instead
  // of falling through to the raw-text input below. See
  // `looseFkEditEnrichment.ts`'s `resolveEditPredicate` header for why a plain
  // `predicateToFilterCondition`-only gate used to leave the PG fallback
  // unreachable in production.
  const engine = sqlEngineToDatabaseEngineId(editContext.service.engineId);
  const cols = useVqeFilterColumns(editContext);
  const canEditAsChip = useMemo(
    () => resolveEditPredicate(text, engine, cols.resolveByField) !== null,
    [text, engine, cols.resolveByField],
  );

  // Round-trippable predicate → structured chip editor.
  if (canEditAsChip) {
    return (
      <VqeFilterChipBuilder
        mode="edit"
        testId={`${testId}-chip`}
        editContext={editContext}
        initialPredicate={text}
        onCommit={onChipCommit}
        onCancel={onChipCancel}
      />
    );
  }

  // Non-round-trippable predicate → inline text input fallback. The
  // `escapeClaimProps` marker lets `CompactVqePopover`'s `onEscapeKeyDown`
  // claim Escape for THIS input while it's mounted (F1 — Escape-in-popover
  // seam consolidation) so Escape cancels the edit only, never the ancestor
  // popover; inert (and harmless) outside a compact-popover host.
  return (
    <input
      ref={inputRef}
      type="text"
      value={value}
      onChange={(e) => onValueChange(e.target.value)}
      onBlur={onInputCommit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          onInputCommit();
        } else if (e.key === 'Escape') {
          e.preventDefault();
          onInputCancel();
        }
      }}
      data-testid={`${testId}-input`}
      className="rounded border border-input bg-background px-1.5 py-0.5 font-mono text-xs"
      aria-label="Edit filter predicate"
      {...escapeClaimProps}
    />
  );
}

export function FilterEditablePill({
  testId,
  text,
  fragmentIndex,
  editContext,
  clause = 'where',
}: FilterEditablePillProps) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(text);
  const inputRef = useRef<HTMLInputElement | null>(null);

  // Reset draft value when the upstream text changes (e.g. round-trip
  // re-renders from a re-parse). Only mirror outside edit mode so an in-flight
  // edit isn't overwritten.
  useEffect(() => {
    if (!editing) setValue(text);
  }, [text, editing]);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  // `clause` defaults to `'where'` (SR-Edit's absent-⇒-'where' convention), so
  // the WHERE call site keeps its original 3-argument shape (no options object)
  // and only HAVING carries the discriminator.
  const commitModify = (newText: string): void => {
    if (clause === 'having') {
      void modifyFilter(fragmentIndex, newText, toCommitCtx(editContext), { clause: 'having' });
    } else {
      void modifyFilter(fragmentIndex, newText, toCommitCtx(editContext));
    }
  };

  const commit = (): void => {
    const trimmed = value.trim();
    if (trimmed.length === 0 || trimmed === text.trim()) {
      setEditing(false);
      setValue(text);
      return;
    }
    setEditing(false);
    commitModify(trimmed);
  };

  const cancel = (): void => {
    setEditing(false);
    setValue(text);
  };

  if (!editing) {
    return (
      <VqeTooltip content="Click to edit predicate">
        <button
          type="button"
          className="font-mono text-xs hover:underline"
          data-testid={testId}
          onClick={() => setEditing(true)}
        >
          {text}
          <PillValueLabel text={text} />
        </button>
      </VqeTooltip>
    );
  }

  // Editing — the heavy `useVqeFilterColumns`/resolve work lives in this
  // edit-time-only child so idle pills never run it.
  return (
    <FilterPillEditor
      testId={testId}
      text={text}
      value={value}
      onValueChange={setValue}
      inputRef={inputRef}
      editContext={editContext}
      onChipCommit={(newText) => {
        setEditing(false);
        commitModify(newText);
      }}
      onChipCancel={cancel}
      onInputCommit={commit}
      onInputCancel={cancel}
    />
  );
}
